// The Help pane's outline (System context PR B): one ring drawn around a
// control the person is being shown, found the way a screen reader finds it
// (voice/screenControls: role and accessible name), so any labelled control
// can be shown without the page declaring it. The ring is one fixed div that
// follows the control while the page scrolls or reflows; it never takes a
// click, and is marked data-voice="off" so no screen tool reads it.

import { controlLabel, scanControls } from '../voice/screenControls'

export const OUTLINE_ID = 'help-outline'
/** The info blue (theme info.main), the colour every button uses. */
const OUTLINE_COLOR = '#0288d1'
const PAD = 4

export type OutlineResult =
  | { ok: true; label: string }
  | { ok: false; error: string; candidates: string[] }

const norm = (s: string) => s.replace(/\s+/g, ' ').trim().toLowerCase()

/**
 * The visible control whose on-screen label best matches `label`: the same
 * label, else one that starts with it, else one that contains it. Controls
 * inside the Help pane itself are never matched.
 */
export function findControl(label: string, root: ParentNode = document): Element | undefined {
  const want = norm(label)
  if (!want) return undefined
  const scanned = scanControls(root).filter((s) => s.control.label && !s.el.closest('[data-help-pane]'))
  const by = (test: (l: string) => boolean) => scanned.find((s) => test(norm(s.control.label)))?.el
  return by((l) => l === want) ?? by((l) => l.startsWith(want)) ?? by((l) => l.includes(want))
}

let tracked: Element | null = null
let frame = 0
let lastRect = ''

function ring(): HTMLDivElement {
  let el = document.getElementById(OUTLINE_ID) as HTMLDivElement | null
  if (el) return el
  el = document.createElement('div')
  el.id = OUTLINE_ID
  el.setAttribute('data-voice', 'off')
  el.setAttribute('aria-hidden', 'true')
  Object.assign(el.style, {
    position: 'fixed',
    pointerEvents: 'none',
    zIndex: '1301',
    border: `3px solid ${OUTLINE_COLOR}`,
    borderRadius: '10px',
    boxShadow: `0 0 0 4px ${OUTLINE_COLOR}33`,
    transition: 'top 120ms ease, left 120ms ease, width 120ms ease, height 120ms ease',
  })
  document.body.appendChild(el)
  return el
}

// Follow the control each frame, writing only when its box moved.
function follow() {
  const el = document.getElementById(OUTLINE_ID)
  if (!tracked || !el) return
  if (!tracked.isConnected) {
    clearOutline()
    return
  }
  const r = tracked.getBoundingClientRect()
  const key = `${r.top}|${r.left}|${r.width}|${r.height}`
  if (key !== lastRect) {
    lastRect = key
    Object.assign(el.style, {
      top: `${r.top - PAD}px`,
      left: `${r.left - PAD}px`,
      width: `${r.width + PAD * 2}px`,
      height: `${r.height + PAD * 2}px`,
    })
  }
  frame = requestAnimationFrame(follow)
}

/** Ring the control labelled `label` and scroll it into view. */
export function outlineControl(label: string, root: ParentNode = document): OutlineResult {
  const el = findControl(label, root)
  if (!el) {
    const candidates = scanControls(root)
      .map((s) => s.control.label)
      .filter((l) => l && l.length > 1)
      .slice(0, 20)
    return { ok: false, error: `no control labelled "${label}" is on screen`, candidates }
  }
  clearOutline()
  tracked = el
  ;(el as HTMLElement).scrollIntoView?.({ block: 'center', inline: 'nearest', behavior: 'smooth' })
  ring()
  follow()
  return { ok: true, label: controlLabel(el) }
}

/** Remove the ring, if one is drawn. */
export function clearOutline() {
  if (frame) cancelAnimationFrame(frame)
  frame = 0
  tracked = null
  lastRect = ''
  document.getElementById(OUTLINE_ID)?.remove()
}
